import React, { useMemo, useState } from "react";
import { format, isToday, isYesterday } from "date-fns";
import { Plus, Trash2, Pencil, X, Check, MessageCircle, ShieldCheck, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { chatPreview } from "@/lib/chats";
import CreateChatPanel, { userFacingRegisterLabel } from "@/components/CreateChatPanel";
import { REGISTERS } from "@/lib/registers";

/**
 * Compact "WhatsApp-style" timestamp.
 * Today        → "8:12 AM"
 * Yesterday    → "Yesterday"
 * < 7 days     → "Thu"
 * older        → "May 12"
 */
function formatActivity(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        if (isToday(d)) return format(d, "h:mm a");
        if (isYesterday(d)) return "Yesterday";
        const ageDays = (Date.now() - d.getTime()) / 86400000;
        if (ageDays < 7) return format(d, "EEE");
        return format(d, "MMM d");
    } catch {
        return "";
    }
}

const ChatList = ({
    chats,
    activeId,
    onSelect,
    onNew,
    onRename,
    onDelete,
    onSetRegister,
    onUpdateContacts,
    creating: creatingProp,
    onRequestCreate,
    onCloseCreate,
    className = "",
}) => {
    const [renamingId, setRenamingId] = useState(null);
    const [draftName, setDraftName] = useState("");
    const [renameRegisterPickerOpen, setRenameRegisterPickerOpen] = useState(
        false,
    );
    const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
    const [query, setQuery] = useState("");
    // Panel state can be controlled by the parent (lifted from ToneGuardPage)
    // OR fall back to a local state for callers that don't pass the props.
    // Prefer parent props when present so multiple entry points (sidebar
    // buttons + DesktopEmptyState CTA) stay in sync.
    const [localCreating, setLocalCreating] = useState(false);
    const creating =
        typeof creatingProp === "boolean" ? creatingProp : localCreating;
    const openCreatePanel = () => {
        if (onRequestCreate) onRequestCreate();
        else setLocalCreating(true);
    };
    const closeCreatePanel = () => {
        if (onCloseCreate) onCloseCreate();
        else setLocalCreating(false);
    };

    const filteredChats = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return chats;
        return chats.filter((c) => {
            // Name match (primary)
            if ((c.name || "").toLowerCase().includes(q)) return true;
            // Last-message preview match (secondary)
            const preview = chatPreview(c).toLowerCase();
            return preview.includes(q);
        });
    }, [chats, query]);

    const startRename = (chat) => {
        setRenamingId(chat.id);
        setDraftName(chat.name);
        setRenameRegisterPickerOpen(false);
    };
    const commitRename = () => {
        if (renamingId) onRename(renamingId, draftName);
        setRenamingId(null);
        setDraftName("");
        setRenameRegisterPickerOpen(false);
    };
    const cancelRename = () => {
        setRenamingId(null);
        setDraftName("");
        setRenameRegisterPickerOpen(false);
    };

    // Legacy local fallback removed — `openCreatePanel` / `closeCreatePanel`
    // declared above route through parent props when present.
    const handleCreateCommit = (name, opts) => {
        onNew?.(name, opts);
        closeCreatePanel();
    };

    return (
        <div
            data-testid="chat-list"
            className={`flex flex-col h-full min-h-0 bg-card ${className}`}
        >
            <div className="px-4 sm:px-5 py-3.5 border-b border-border flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2.5 min-w-0">
                    <span
                        aria-hidden
                        className="h-8 w-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 text-primary"
                    >
                        <ShieldCheck className="h-4 w-4" />
                    </span>
                    <div className="leading-tight min-w-0">
                        <h2 className="font-serif text-[18px] text-foreground truncate">
                            ToneGuard
                        </h2>
                        <p
                            data-testid="sidebar-tagline"
                            className="text-[9.5px] uppercase tracking-[0.12em] text-muted-foreground leading-tight"
                        >
                            Better messages.
                            <br />
                            Better connections.
                        </p>
                    </div>
                </div>
                <Button
                    data-testid="btn-new-chat"
                    size="sm"
                    onClick={openCreatePanel}
                    className="rounded-full gap-1.5 h-8 px-3 text-[13px] shrink-0"
                >
                    <Plus className="h-3.5 w-3.5" />
                    New
                </Button>
            </div>

            {creating && (
                <CreateChatPanel
                    onCreate={handleCreateCommit}
                    onCancel={closeCreatePanel}
                />
            )}

            {/* Search — appears only when there's at least one chat to search.
                Keeps the empty-state clean and on-message. */}
            {chats.length > 0 && (
                <div
                    data-testid="chat-search-wrapper"
                    className="px-4 sm:px-5 py-2.5 border-b border-border/60 shrink-0"
                >
                    <div className="relative">
                        <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none"
                            aria-hidden
                        />
                        <Input
                            data-testid="input-chat-search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search chats"
                            aria-label="Search chats"
                            className="h-8 pl-8 pr-8 text-[13px] bg-muted/40 border-transparent focus-visible:bg-card focus-visible:border-border placeholder:text-muted-foreground/70"
                        />
                        {query && (
                            <button
                                type="button"
                                data-testid="btn-clear-search"
                                onClick={() => setQuery("")}
                                aria-label="Clear search"
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/60"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className="flex-1 min-h-0 overflow-y-auto">
                {chats.length === 0 ? (
                    <div className="p-6 sm:p-8 text-center text-muted-foreground flex flex-col items-center h-full justify-center">
                        <div className="h-12 w-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-3">
                            <MessageCircle className="h-5 w-5 text-primary" />
                        </div>
                        <p className="text-sm mb-4 max-w-[220px]">
                            Create one chat per person — a relationship-aware
                            way to draft messages.
                        </p>
                        <Button
                            data-testid="btn-empty-new-chat"
                            onClick={openCreatePanel}
                            className="rounded-full gap-1.5"
                        >
                            <Plus className="h-4 w-4" />
                            Create your first chat
                        </Button>
                    </div>
                ) : filteredChats.length === 0 ? (
                    <div
                        data-testid="search-empty-state"
                        className="px-4 sm:px-5 py-8 text-center text-muted-foreground"
                    >
                        <p className="text-sm">
                            No chats match{" "}
                            <span className="text-foreground font-medium">
                                {`"${query}"`}
                            </span>
                        </p>
                        <button
                            type="button"
                            onClick={() => setQuery("")}
                            className="mt-2 text-[12px] text-[#4B6143] hover:underline"
                        >
                            Clear search
                        </button>
                    </div>
                ) : (
                    <ul role="list" className="divide-y divide-border/60">
                        {filteredChats.map((c) => {
                            const isActive = c.id === activeId;
                            const renaming = renamingId === c.id;
                            const confirming = confirmingDeleteId === c.id;
                            return (
                                <li
                                    key={c.id}
                                    data-testid={`chat-list-item`}
                                    data-chat-id={c.id}
                                    data-active={isActive}
                                    className={`group relative px-4 sm:px-5 py-3 cursor-pointer transition-colors ${
                                        isActive
                                            ? "bg-[#EAF1E8]/45"
                                            : "hover:bg-stone-50"
                                    }`}
                                    onClick={() => {
                                        if (!renaming && !confirming) {
                                            onSelect(c.id);
                                        }
                                    }}
                                >
                                    {renaming ? (
                                        <div
                                            className="flex flex-col gap-2"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Input
                                                    autoFocus
                                                    data-testid="input-rename-chat"
                                                    value={draftName}
                                                    onChange={(e) =>
                                                        setDraftName(e.target.value)
                                                    }
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") commitRename();
                                                        if (e.key === "Escape") cancelRename();
                                                    }}
                                                    placeholder="e.g. Sam tough convo"
                                                    className="h-8 text-sm"
                                                    maxLength={48}
                                                />
                                                <button
                                                    data-testid="btn-confirm-rename"
                                                    onClick={commitRename}
                                                    aria-label="Save name"
                                                    className="p-1 rounded text-[#4B6143] hover:bg-[#EAF1E8]"
                                                >
                                                    <Check className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={cancelRename}
                                                    aria-label="Cancel rename"
                                                    className="p-1 rounded text-muted-foreground hover:bg-stone-100"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </div>
                                            {/* Step 5 — change register from the rename flow. The
                                                line shows the user-facing label only; system names
                                                (Authority/Peer/Familial/Intimate) never appear in UI. */}
                                            <RenameRegisterRow
                                                chat={c}
                                                open={renameRegisterPickerOpen}
                                                onToggle={() =>
                                                    setRenameRegisterPickerOpen((v) => !v)
                                                }
                                                onPick={(next) => {
                                                    onSetRegister?.(c.id, next);
                                                    setRenameRegisterPickerOpen(false);
                                                }}
                                            />
                                            {/* Step 6 — chat-settings contact details
                                                (WhatsApp number + email). Both optional;
                                                editable any time without leaving the
                                                rename row. Saved to MongoDB on save. */}
                                            <ChatContactsRow
                                                chat={c}
                                                onSave={(next) => {
                                                    onUpdateContacts?.(c.id, next);
                                                }}
                                            />
                                        </div>
                                    ) : confirming ? (
                                        <div
                                            className="flex items-center justify-between gap-2"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <span className="text-[13px] text-foreground truncate">
                                                Delete {`"${c.name}"`}?
                                            </span>
                                            <div className="flex items-center gap-1.5 shrink-0">
                                                <button
                                                    data-testid="btn-confirm-delete"
                                                    onClick={() => {
                                                        onDelete(c.id);
                                                        setConfirmingDeleteId(null);
                                                    }}
                                                    className="text-[12px] font-medium text-[#8C3A2F] hover:text-[#5e2a23] rounded px-2 py-0.5"
                                                >
                                                    Delete
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        setConfirmingDeleteId(null)
                                                    }
                                                    className="text-[12px] text-muted-foreground hover:text-foreground rounded px-2 py-0.5"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex items-baseline justify-between gap-2">
                                                <span
                                                    data-testid="chat-name"
                                                    className="font-medium text-foreground truncate"
                                                >
                                                    {c.name}
                                                </span>
                                                <span className="text-[11px] text-muted-foreground shrink-0 tabular-nums">
                                                    {formatActivity(c.lastActivityAt)}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between gap-2 mt-0.5">
                                                <span className="text-[13px] text-muted-foreground truncate flex-1 min-w-0">
                                                    {chatPreview(c)}
                                                </span>
                                                <div className="flex items-center gap-0.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0">
                                                    <button
                                                        data-testid="btn-rename-chat"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            startRename(c);
                                                        }}
                                                        aria-label="Rename chat"
                                                        className="p-1.5 text-muted-foreground hover:text-foreground rounded-full hover:bg-stone-100"
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </button>
                                                    <button
                                                        data-testid="btn-delete-chat"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setConfirmingDeleteId(c.id);
                                                        }}
                                                        aria-label="Delete chat"
                                                        className="p-1.5 text-muted-foreground hover:text-[#8C3A2F] rounded-full hover:bg-stone-100"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
};

export const ProfileTriggerRow = ({ userProfile, onOpenProfile }) => {
    const name = (userProfile?.name || "Friend").trim() || "Friend";
    const initial = name.charAt(0).toUpperCase();
    const photo = userProfile?.photo || null;
    return (
        <div className="shrink-0 border-t border-border/70 bg-card relative z-50">
            <button
                type="button"
                data-testid="btn-profile"
                data-tg-profile-trigger
                onClick={onOpenProfile}
                aria-label="Open profile"
                title="Profile"
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors focus-visible:bg-stone-50 focus-visible:outline-none cursor-pointer"
            >
                <span
                    data-testid="avatar-trigger"
                    className="relative h-9 w-9 rounded-full overflow-hidden bg-[#EAF1E8] border border-[#D5E3D1] flex items-center justify-center shrink-0 text-[#3F523A] font-serif text-[15px] font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                >
                    {photo ? (
                        <img
                            src={photo}
                            alt=""
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <span aria-hidden>{initial}</span>
                    )}
                </span>
                <div className="leading-tight min-w-0 flex-1 text-left">
                    <p className="text-[13.5px] font-semibold text-foreground truncate">
                        {name}
                    </p>
                    <p className="text-[11px] text-muted-foreground truncate">
                        View profile
                    </p>
                </div>
            </button>
        </div>
    );
};

export default ChatList;

// ─── Rename-flow register row (Step 5) ─────────────────────────────────
// Renders inside the inline rename panel. Shows the user-facing label
// ("Work contact" / "Friend" / "Family" / "Close partner" / "Not set"),
// and on Change → reveals 4 mini-buttons that update the register via
// onPick. System names (Authority/Peer/Familial/Intimate) NEVER surface.
const RenameRegisterRow = ({ chat, open, onToggle, onPick }) => {
    const currentLabel = userFacingRegisterLabel(chat.relationship_register);
    return (
        <div
            data-testid="rename-register-row"
            className="rounded-lg bg-stone-50 border border-border/60 px-2.5 py-2"
        >
            <div className="flex items-center justify-between gap-2">
                <p className="text-[11.5px] text-muted-foreground truncate">
                    <span className="font-medium text-foreground/80">
                        Relationship type:
                    </span>{" "}
                    <span data-testid="rename-register-current">
                        {currentLabel}
                    </span>
                </p>
                <button
                    type="button"
                    data-testid="btn-rename-change-register"
                    onClick={onToggle}
                    className="text-[11px] font-medium text-[#4B6143] hover:underline shrink-0"
                >
                    {open ? "Close" : "Change \u2192"}
                </button>
            </div>
            {open && (
                <div
                    data-testid="rename-register-picker"
                    className="mt-2 grid grid-cols-2 gap-1.5"
                >
                    {REGISTERS.map((reg) => {
                        const selected = chat.relationship_register === reg.key;
                        return (
                            <button
                                key={reg.key}
                                type="button"
                                data-testid={`rename-register-pick-${reg.key}`}
                                onClick={() => onPick(reg.key)}
                                aria-pressed={selected}
                                className={[
                                    "rounded-md px-2 py-1.5 text-[11.5px] font-medium text-left transition-colors border",
                                    selected
                                        ? "bg-[#EAF1E8] border-[#D5E3D1] text-[#3F523A]"
                                        : "bg-card border-border hover:bg-stone-50",
                                ].join(" ")}
                            >
                                {reg.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/**
 * ChatContactsRow — inline editor inside the chat rename row for the two
 * optional contact fields (WhatsApp number + email). Per spec: shows
 * "Not added" placeholder when empty, an "Edit" pencil flips the row to
 * input mode, and Save persists to MongoDB via the parent callback.
 *
 * Stored only in the user's account, never shared. The SendPanel reads
 * these fields directly off the chat object on render.
 */
const ChatContactsField = ({
    label,
    value,
    placeholder,
    type,
    testId,
    editing,
    onStartEdit,
    draft,
    setDraft,
    onSave,
    onCancel,
}) => (
    <div className="flex items-center justify-between gap-2 py-1">
        <span className="text-[12px] text-muted-foreground shrink-0">
            {label}
        </span>
        {editing ? (
            <div className="flex items-center gap-1 flex-1 min-w-0">
                <input
                    data-testid={`${testId}-input`}
                    type={type}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={placeholder}
                    autoFocus
                    className="flex-1 min-w-0 text-[12.5px] px-2 py-1 bg-card border border-border rounded outline-none focus:border-[#5E7153] focus:ring-1 focus:ring-[#5E7153]/30"
                />
                <button
                    type="button"
                    data-testid={`${testId}-save`}
                    onClick={onSave}
                    aria-label="Save"
                    className="p-1 rounded text-[#4B6143] hover:bg-[#EAF1E8]"
                >
                    <Check className="h-3.5 w-3.5" />
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    aria-label="Cancel"
                    className="p-1 rounded text-muted-foreground hover:bg-stone-100"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>
        ) : (
            <button
                type="button"
                data-testid={`${testId}-edit`}
                onClick={onStartEdit}
                className="flex-1 min-w-0 text-right text-[12.5px] text-foreground/80 hover:text-foreground underline underline-offset-2 decoration-dotted truncate"
            >
                {value || (
                    <span className="text-muted-foreground italic">
                        Not added — Edit →
                    </span>
                )}
            </button>
        )}
    </div>
);

const ChatContactsRow = ({ chat, onSave }) => {
    const [editing, setEditing] = useState(null); // "phone" | "email" | null
    const [phone, setPhone] = useState(chat?.phone_number || "");
    const [email, setEmail] = useState(chat?.email_address || "");

    // Re-seed local state when the source chat changes (different chat opens
    // its rename row).
    React.useEffect(() => {
        setPhone(chat?.phone_number || "");
        setEmail(chat?.email_address || "");
        setEditing(null);
    }, [chat?.id, chat?.phone_number, chat?.email_address]);

    const commitField = () => {
        onSave?.({
            phone_number: phone.trim() || null,
            email_address: email.trim() || null,
        });
        setEditing(null);
    };

    const cancelField = (field) => {
        if (field === "phone") setPhone(chat?.phone_number || "");
        if (field === "email") setEmail(chat?.email_address || "");
        setEditing(null);
    };

    return (
        <div
            data-testid="chat-contacts-row"
            className="mt-1.5 pt-1.5 border-t border-border/50"
        >
            <ChatContactsField
                label="WhatsApp"
                value={phone}
                placeholder="+91 98765 43210"
                type="tel"
                testId="chat-contact-phone"
                editing={editing === "phone"}
                onStartEdit={() => setEditing("phone")}
                draft={phone}
                setDraft={setPhone}
                onSave={commitField}
                onCancel={() => cancelField("phone")}
            />
            <ChatContactsField
                label="Email"
                value={email}
                placeholder="name@gmail.com"
                type="email"
                testId="chat-contact-email"
                editing={editing === "email"}
                onStartEdit={() => setEditing("email")}
                draft={email}
                setDraft={setEmail}
                onSave={commitField}
                onCancel={() => cancelField("email")}
            />
        </div>
    );
};
